import { CrossFrameUtils } from '@utils/CrossFrameUtils';

export abstract class BaseCoralHandler {
    public static storeId;
    public static amazonDomain;
    protected static authMethod: string;
    protected static authValidator: () => boolean;
    protected static handlerName: string;

    /**
     * Sends a request through cross-frame with IIB authentication.
     *
     * @param {object} request - The request to send.
     * @param {object} ihmCoralClient - The coral client to use.
     * @param {object} extraOptions - Optional extra options for the request.
     *  Example
     *      {
     *          sensitive: {boolean}, If set to true, it will not log the data contained in the request.
     *      }
     */
    public static async sendRequest(request: object, ihmCoralClient: object, extraOptions?: object) {
        try {
            return this.makeServiceCall(request, ihmCoralClient, extraOptions);
        } catch (error) {
            console.error(error);
            return Promise.reject(error);
        }
    }

    public static resolveStoreSpecificEndpoint(endpoint, options) {
        /**
         *  We use a url like "https://PARTITION.myService.amazon.com" in the client config
         *  so that older clients can continue to use the url without breaking.  The store-
         *  specific clients will replace it with the store id, or fallback to the global
         *  url if a store doesn't exist in the call.
         */
        const PARTITION_REPLACEMENT_STRING = 'PARTITION';

        /**
         * Replaces 'PARTITION' with the storeId defined in the options object (options.data.storeId)
         */
        if (options.data.storeId === undefined || options.data.storeId === null) {
            return endpoint;
        }

        /**
         * Honor the disable flag if set
         */
        if (options.disableStoreSpecificRouting !== undefined) {
            return endpoint;
        }

        /**
         * Convert "https://PARTITION.foo.amazon.com" to "https://lab05.foo.amazon.com"
         * replace _s with -s due to DNS specification, matching LPT behaviour
         */
        const normalizedStoreId = options.data.storeId.toLowerCase().replace(/[^a-z0-9]/g, '-');
        endpoint = endpoint.replace(PARTITION_REPLACEMENT_STRING, normalizedStoreId);
        return endpoint;
    }

    protected static getJsonFromResponse(response, sensitive) {
        let processedResponse;
        let jsonResponse;
        try {
            jsonResponse = JSON.parse(response || '{}');
        } catch (e) {
            console.error('getJsonFromResponse: Error parsing response', CrossFrameUtils.getDataForLoggingEvent(sensitive, e));
        }

        if (jsonResponse.metadata && jsonResponse.metadata.hasMetadata) {
            processedResponse = jsonResponse;
        } else {
            processedResponse = {
                metadata: {
                    isOldFormatResponse: true,
                    hasMetadata: true,
                    statusCode: jsonResponse.status,
                },
                data: jsonResponse,
            };
        }
        return processedResponse;
    }

    protected static getClientEndpoint(client, request) {
        // TODO: STORE ISOLATION MIGRATION: Delete branch when IhmJSCoralConfig has been deprecated
        if (!client.hasOwnProperty('config')) {
            return client.endpoint;
        }
        const configEndpoint = client.config.endpoint();
        const resolvedEndpoint = this.resolveStoreSpecificEndpoint(configEndpoint, request);
        console.debug(`Client config endpoint: '${configEndpoint}'resolved to: '${resolvedEndpoint}'"`);
        return resolvedEndpoint;
    }

    protected static getClientOperationPrefix(client) {
        // TODO: STORE ISOLATION MIGRATION: Delete branch when IhmJSCoralConfig has been deprecated
        if (!client.hasOwnProperty('config')) {
            return client.operationPrefix;
        }
        return client.config.operationPrefix();
    }

    protected static async makeServiceCall(request, ihmCoralClient, extraOptions?) {
        extraOptions = extraOptions || {};
        extraOptions.sensitive = !!extraOptions.sensitive;
        console.debug(CrossFrameUtils.getDataForLoggingEvent(extraOptions.sensitive, request));

        if (!this.authValidator()) {
            const authType = this.authMethod.replace('make', '').replace('AjaxRequest', '');
            return Promise.reject(`Not executing in ${authType} Auth context`);
        }

        const requestHeaders = {
            'X-Amz-Target': `${this.getClientOperationPrefix(ihmCoralClient)}.${request.operation}`,
        };

        const ajaxRequestData = {
            endpoint: this.getClientEndpoint(ihmCoralClient, request),
            requestData: request.data,
            requestHeaders,
            requestExtra: { useMetadataResponse: true },
        };

        try {
            const response = await CrossFrameUtils.sendMessageToOtherFrame({
                frame: window.top,
                message: {
                    method: this.authMethod,
                    data: ajaxRequestData,
                    sensitive: extraOptions.sensitive,
                },
            });

            const jsonResponse = this.getJsonFromResponse(response, extraOptions.sensitive);
            if (jsonResponse.data && jsonResponse.data.__type) {
                return Promise.reject(jsonResponse);
            }
            return Promise.resolve(jsonResponse);
        } catch (error) {
            const jsonResponse = this.getJsonFromResponse(error, extraOptions.sensitive);
            console.debug(`${this.handlerName}: makingServiceCall failed`);
            return Promise.reject(jsonResponse);
        }
    }
}
